import { handleSubscribe } from "@api/Billing";
import { useMutation } from "@apollo/client/react";
import {
  DSButton,
  DSCardWrapper,
  DSCheckbox,
  DSDropdown,
} from "@components/design-system/molecules";
import {
  faSort,
  faMinusCircle,
  faBan,
  faCheck,
  faTimes,
  faMoneyBill,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DEACTIVATE_USER from "@graphql/mutations/DeactivateUser";
import REMOVE_USER_FROM_TEAM from "@graphql/mutations/RemoveUserFromTeam";
import { Tooltip } from "@mui/material";
import { captureException } from "@sentry/react";
import { UNLIMITED_PRICE_ID_MONTHLY } from "@src/constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

import { mapRoleIdToRole, nonBillableRoles } from "./utils";
import UPDATE_USER_TEAM from "../../../graphql/mutations/UpdateUserTeam";
import { useAuth, useOrganization } from "../../../hooks";
import { useTeam } from "../../../hooks/use-team";
import { alert } from "../../common/Alert";
import { DSModal } from "../../design-system/organisms";
import { getUserModificationBillingText } from "../TeamModal/utils";

const ROLE_OPTIONS = [
  { label: mapRoleIdToRole(1), value: 1 },
  { label: mapRoleIdToRole(2), value: 2 },
  { label: mapRoleIdToRole(4), value: 4 },
  { label: mapRoleIdToRole(5), value: 5 },
];

const ACCOUNT_TYPE_OPTIONS = [
  { label: "Free Plan", value: "basic" },
  { label: "Pro Plan", value: "unlimited" },
];

export function RoleDropdown({ user, onChange, error = null }) {
  return (
    <div
      className={
        error && !user?.roleId
          ? "ring-2 ring-danger rounded-standard"
          : ""
      }
    >
      <DSDropdown
        options={ROLE_OPTIONS}
        value={user?.roleId}
        onChange={(value) => onChange(null, value)}
        width="w-full"
        dataTestId="role-dropdown"
        size="sm"
      />
    </div>
  );
}

export function AccountTypeDropdown({
  user,
  onChange,
  disabled = false,
  error = null,
}) {
  return (
    <div
      className={
        error && !user.accountType
          ? "ring-2 ring-danger rounded-standard"
          : ""
      }
    >
      <DSDropdown
        options={ACCOUNT_TYPE_OPTIONS}
        value={user.accountType}
        onChange={(value) => onChange(null, value)}
        disabled={disabled}
        width="w-full"
        dataTestId="account-type-dropdown"
        size="sm"
      />
    </div>
  );
}

export function EditExistingTeamMembers({
  team,
  isCurrentUserAnAdmin,
  refetchTeam,
}) {
  const location = useLocation();
  const currentPath = location.pathname;
  //constants
  const teamUuid = team?.uuid;
  const { allowUserDeactivation } = useFlags();
  const [editingUser, setEditingUser] = useState({});

  //state
  const billingIncrementText = editingUser?.uuid
    ? getUserModificationBillingText(editingUser)
    : "";
  const [isShowingUpgradeModal, setIsShowingUpgradeModal] =
    useState(false);
  const [currentSortKey, setCurrentSortKey] = useState(null);
  const [isSortReverse, setIsSortReverse] = useState(false);
  const [isShowingRemoveModal, setIsShowingRemoveModal] =
    useState(false);
  const [isShowingDeactivateModal, setIsShowingDeactivateModal] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const savingUser = useRef(false);

  //hooks
  const { isOrgAdmin } = useOrganization();
  const { userUuid } = useAuth();
  const { refreshUserTeams } = useTeam();

  //mutations
  const [removeUserFromTeam] = useMutation(REMOVE_USER_FROM_TEAM);
  const [updateUserTeam] = useMutation(UPDATE_USER_TEAM);
  const [deactivateUser] = useMutation(DEACTIVATE_USER);

  const sortedUsers = useMemo(() => {
    const users = team?.users || [];
    if (!currentSortKey) {
      return users;
    }

    return [...users].sort((a, b) => {
      const aValue = a[currentSortKey] ?? "";
      const bValue = b[currentSortKey] ?? "";

      if (
        typeof aValue === "boolean" &&
        typeof bValue === "boolean"
      ) {
        return isSortReverse
          ? (bValue ? 1 : 0) - (aValue ? 1 : 0)
          : (aValue ? 1 : 0) - (bValue ? 1 : 0);
      }
      if (typeof aValue === "number" || typeof bValue === "number") {
        return isSortReverse ? bValue - aValue : aValue - bValue;
      }
      return isSortReverse
        ? String(bValue).localeCompare(String(aValue))
        : String(aValue).localeCompare(String(bValue));
    });
  }, [team?.users, currentSortKey, isSortReverse]);

  //functions
  const sortUsers = (sortBy) => {
    setIsSortReverse((prev) =>
      currentSortKey === sortBy ? !prev : false,
    );
    setCurrentSortKey(sortBy);
  };

  const handleRemoveClick = (user) => {
    setSelectedUser(user);
    setIsShowingRemoveModal(true);
  };

  const handleSaveUser = async (user) => {
    if (savingUser.current) {
      return;
    }
    savingUser.current = true;
    try {
      const { data } = await updateUserTeam({
        variables: {
          teamUuid: teamUuid,
          userUuid: user.uuid,
          ...editingUser,
        },
      });
      refreshUserTeams();
      refetchTeam();
      const billingImplication =
        data?.updateUserTeam?.billingImplication;

      alert(
        "success",
        `${user?.firstName} ${user?.lastName} has been modified. ${
          billingImplication === 0
            ? ""
            : "Your billing has been updated accordingly."
        }`,
      );
      mixpanel.track("Edit user", {
        teamUuid: teamUuid,
        userUuid: user.uuid,
      });
      cancelEditingUser();
    } catch (error) {
      if (
        error.message ===
        "Please upgrade your plan to modify your team members' subscriptions"
      ) {
        setIsShowingUpgradeModal(true);
      } else {
        alert("error", error.message);
      }
      mixpanel.track("Edit user error", {
        teamUuid: teamUuid,
        userUuid: user.uuid,
        orgUuid: team?.orgUuid || team?.organization?.uuid,
        error: error,
      });
    }
    savingUser.current = false;
  };

  const handleEditClick = (user) => {
    setEditingUser({
      uuid: user.uuid,
      roleId: user.roleId,
      accountType: user.accountType,
      isAdmin: user.isAdmin,
      originalAccountType: user.accountType,
      originalRoleId: user.roleId,
    });
  };

  const cancelEditingUser = () => {
    setEditingUser({});
  };

  const handleDeactivateClick = (user) => {
    setSelectedUser(user);
    setIsShowingDeactivateModal(true);
    refreshUserTeams();
  };

  const handleConfirmRemoveFromTeam = async () => {
    try {
      await removeUserFromTeam({
        variables: {
          teamUuid: teamUuid,
          userUuid: selectedUser?.uuid,
        },
      });
      setIsShowingRemoveModal(false);
      refreshUserTeams();
      refetchTeam();

      alert(
        "success",
        `${selectedUser?.firstName} ${selectedUser?.lastName} has been removed from the team.`,
      );
      mixpanel.track("Remove user from team", {
        teamUuid: teamUuid,
        userUuid: selectedUser?.uuid,
      });
    } catch (error) {
      alert(
        "error",
        `Error removing user from team: ${error.message}`,
      );
      mixpanel.track("Remove user from team error", {
        teamUuid: teamUuid,
        userUuid: selectedUser?.uuid,
        orgUuid: team?.orgUuid || team?.organization?.uuid,
        error: error,
      });
    }
  };

  const handleConfirmDeactivateUser = async () => {
    const rmTeamUserInput = {
      teamUuid: team?.uuid,
      userUuid: selectedUser?.uuid,
      orgUuid: team?.orgUuid || team?.organization?.uuid,
    };
    try {
      await deactivateUser({
        variables: { rmTeamUserInput },
      });
      setIsShowingDeactivateModal(false);
      refreshUserTeams();
      refetchTeam();
      alert(
        "success",
        `${selectedUser?.firstName} ${selectedUser?.lastName} has been deactivated.`,
      );
      mixpanel.track("Deactivate user", {
        teamUuid: teamUuid,
        userUuid: selectedUser?.uuid,
      });
    } catch (error) {
      alert("error", `Error deactivating user: ${error.message}`);
      mixpanel.track("Deactivate user error", {
        teamUuid: teamUuid,
        userUuid: selectedUser?.uuid,
        orgUuid: team?.orgUuid || team?.organization?.uuid,
        error: error,
      });
      captureException(error);
    }
  };

  //render
  return (
    <div className="my-md">
      <DSCardWrapper
        variant="default"
        padding="py-sm px-sm"
        dataTestId="existing-team-members-table"
      >
        <table className="table-fixed w-full text-primary text-left">
          <thead>
            <tr className="border-b border-primary">
              <TableHeader
                title="First Name"
                sortBy="firstName"
                sortUsers={sortUsers}
              />
              <TableHeader
                title="Last Name"
                sortBy="lastName"
                sortUsers={sortUsers}
              />
              <TableHeader
                title="Email"
                sortBy="email"
                sortUsers={sortUsers}
              />
              <TableHeader
                title="SOAPs Created"
                sortBy="numAppointmentsUsed"
                sortUsers={sortUsers}
                isCentered={true}
              />
              <TableHeader
                title="Role"
                sortBy="roleId"
                sortUsers={sortUsers}
              />
              <TableHeader
                title="Team Admin"
                sortBy="isAdmin"
                sortUsers={sortUsers}
                isCentered={true}
              />
              {team?.hasBillingSetup && (
                <TableHeader
                  title="Account Plan"
                  sortBy="accountType"
                  sortUsers={sortUsers}
                />
              )}
              {isCurrentUserAnAdmin && (
                <>
                  <th className="font-semibold text-sm text-center">
                    Remove
                  </th>
                  {allowUserDeactivation && isOrgAdmin && (
                    <th className="font-semibold text-sm text-center">
                      Deactivate
                    </th>
                  )}
                  <th className="font-semibold text-sm"></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedUsers?.map((user, index) => {
              const isCurrentUser = user.uuid === userUuid;
              const isEditingUser = user.uuid === editingUser?.uuid;
              const cannotManageUser =
                isCurrentUser || (user?.isAdmin && !isOrgAdmin);
              const manageUserClass = cannotManageUser
                ? "text-placeholder cursor-not-allowed"
                : "text-danger cursor-pointer hover:text-red-700";
              return (
                <tr
                  key={index}
                  data-testid={`team-member-row-${user.uuid}`}
                  data-testid-name={`team-member-${user.firstName}-${user.lastName}`}
                  className="even:bg-transparent odd:bg-primary"
                >
                  <td>
                    <h5 className="text-sm pl-2 pr-2 truncate overflow-hidden text-ellipsis">
                      {user?.firstName}
                    </h5>
                  </td>
                  <td>
                    <h5 className="text-sm pr-2 truncate overflow-hidden text-ellipsis">
                      {user?.lastName}
                    </h5>
                  </td>
                  <td className="py-sm">
                    <h5 className="text-sm pr-2 truncate overflow-hidden text-ellipsis">
                      {user?.email}
                    </h5>
                  </td>
                  <td className="text-center pr-4">
                    <h5 className="text-sm">
                      {user?.numAppointmentsUsed}
                    </h5>
                  </td>

                  <td>
                    {isEditingUser ? (
                      <RoleDropdown
                        user={editingUser}
                        onChange={(_, value) => {
                          setEditingUser((prev) => ({
                            ...prev,
                            roleId: value,
                          }));
                        }}
                      />
                    ) : (
                      <h5
                        className="text-sm"
                        data-testid={`user-role-${user.uuid}`}
                        data-testid-name={`user-role-${user.firstName}-${user.lastName}`}
                      >
                        {mapRoleIdToRole(user?.roleId)}
                      </h5>
                    )}
                  </td>
                  <td className="text-center pr-4">
                    {isEditingUser ? (
                      <div className="flex justify-center items-center">
                        <DSCheckbox
                          checked={editingUser.isAdmin || false}
                          onChange={(e) => {
                            setEditingUser((prev) => ({
                              ...prev,
                              isAdmin: e.target.checked,
                            }));
                          }}
                          dataTestId="admin-checkbox"
                          aria-label="Team Admin"
                        />
                      </div>
                    ) : (
                      <h5 className="text-sm flex justify-center items-center">
                        {user?.isAdmin ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-success"
                            data-testid={`is-admin-icon-${user.uuid}`}
                            data-testid-name={`is-admin-icon-${user.firstName}-${user.lastName}`}
                          />
                        ) : (
                          ""
                        )}
                      </h5>
                    )}
                  </td>

                  {team?.hasBillingSetup && (
                    <td className="pr-2">
                      {isEditingUser ? (
                        <AccountTypeDropdown
                          user={editingUser}
                          onChange={(_, value) => {
                            setEditingUser((prev) => ({
                              ...prev,
                              accountType: value,
                            }));
                          }}
                        />
                      ) : (
                        <h5 className="text-sm">
                          {!user?.isSelfSubscribed &&
                            !nonBillableRoles.includes(
                              user?.roleId,
                            ) &&
                            user?.accountType === "unlimited" && (
                              <Tooltip
                                arrow
                                title="This user's subscription is being paid for"
                              >
                                <FontAwesomeIcon
                                  icon={faMoneyBill}
                                  className="text-success mr-1"
                                ></FontAwesomeIcon>
                              </Tooltip>
                            )}
                          {!user?.isSelfSubscribed &&
                            nonBillableRoles.includes(user?.roleId) &&
                            user?.accountType === "unlimited" && (
                              <Tooltip
                                arrow
                                title="This user's subscription is free"
                              >
                                <FontAwesomeIcon
                                  icon={faMoneyBill}
                                  className="mr-1"
                                ></FontAwesomeIcon>
                              </Tooltip>
                            )}
                          {user?.isSelfSubscribed &&
                            user?.accountType === "unlimited" && (
                              <Tooltip
                                arrow
                                title="This user is paying for their own subscription"
                              >
                                <FontAwesomeIcon
                                  icon={faMoneyBill}
                                  className="text-danger mr-1"
                                ></FontAwesomeIcon>
                              </Tooltip>
                            )}
                          {user?.accountType === "basic"
                            ? "Free Plan"
                            : user?.accountType === "unlimited"
                              ? "Pro Plan"
                              : "Free Plan"}
                        </h5>
                      )}
                    </td>
                  )}
                  {isCurrentUserAnAdmin && (
                    <>
                      <td className="text-center">
                        <button
                          type="button"
                          disabled={cannotManageUser}
                          aria-label={`Remove ${user.firstName} ${user.lastName}`}
                          className={manageUserClass}
                          onClick={() => handleRemoveClick(user)}
                          data-testid={`remove-user-${user.uuid}`}
                          data-testid-name={`remove-user-${user.firstName}-${user.lastName}`}
                        >
                          <FontAwesomeIcon icon={faMinusCircle} />
                        </button>
                      </td>
                      {allowUserDeactivation && isOrgAdmin && (
                        <td className="text-center">
                          {user?.isDisabled ? (
                            <Tooltip
                              arrow
                              title="User is deactivated. Please contact support to re-enable the user"
                            >
                              <span data-testid="deactivated-user-icon">
                                <FontAwesomeIcon
                                  icon={faBan}
                                  className="text-decoration-line-through text-secondary cursor-not-allowed pointer-events-none"
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <button
                              type="button"
                              disabled={cannotManageUser}
                              aria-label={`Deactivate ${user.firstName} ${user.lastName}`}
                              className={manageUserClass}
                              onClick={() =>
                                handleDeactivateClick(user)
                              }
                              data-testid={`deactivate-user-${user.uuid}`}
                              data-testid-name={`deactivate-user-${user.firstName}-${user.lastName}`}
                            >
                              <FontAwesomeIcon icon={faBan} />
                            </button>
                          )}
                        </td>
                      )}
                      <td>
                        {user?.isAdmin && !isOrgAdmin ? (
                          ""
                        ) : (
                          <div className="flex justify-center gap-3 px-2">
                            {isEditingUser ? (
                              <div className="flex gap-x-sm">
                                <button
                                  type="reset"
                                  className="flex items-center justify-center w-8 h-8 rounded-full bg-danger-subtle text-danger hover:bg-red-200 dark:hover:bg-red-900/30 transition-all focus:outline-hidden"
                                  onClick={() => cancelEditingUser()}
                                >
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    size="md"
                                  />
                                </button>
                                <button
                                  type="submit"
                                  className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-subtle text-success hover:bg-indigo-200 dark:hover:bg-indigo-900/30 transition-all focus:outline-hidden"
                                  onClick={() => handleSaveUser(user)}
                                  data-testid="save-user-button"
                                >
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    size="md"
                                  />
                                </button>
                              </div>
                            ) : (
                              <DSButton
                                variant="primary"
                                size="sm"
                                disabled={!!editingUser?.uuid}
                                onClick={() => handleEditClick(user)}
                                dataTestId={`edit-user-${user.uuid}`}
                                data-testid-name={`edit-user-${user.firstName}-${user.lastName}`}
                              >
                                Edit
                              </DSButton>
                            )}
                          </div>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {billingIncrementText && (
          <div className="m-md">
            <DSCardWrapper
              variant="warning"
              padding="p-md"
              dataTestId="billing-increment-warning"
            >
              {billingIncrementText}
            </DSCardWrapper>
          </div>
        )}
        {isShowingUpgradeModal && (
          <DSModal
            isOpen={isShowingUpgradeModal}
            onClose={() => setIsShowingUpgradeModal(false)}
            title="Upgrade Your Account"
            dataTestId="upgrade-account-modal"
            actions={[
              {
                label: "I'll Upgrade Later",
                variant: "cancel",
                onClick: () => setIsShowingUpgradeModal(false),
              },
              {
                label: "Upgrade",
                variant: "primary",
                onClick: () => {
                  mixpanel.track("Upgrade Plan Clicked", {
                    featureName: "Team Management",
                  });

                  handleSubscribe({
                    priceId: UNLIMITED_PRICE_ID_MONTHLY,
                    route: currentPath,
                  });
                },
              },
            ]}
          >
            In order to upgrade team members to the Unlimited plan,
            you must first upgrade your own account. If you do not
            want to be the primary billing contact, please set up
            another admin on the team who will become the primary
            billing contact.
          </DSModal>
        )}
        {isShowingRemoveModal && (
          <DSModal
            isOpen={isShowingRemoveModal}
            onClose={() => setIsShowingRemoveModal(false)}
            title="Remove from Team"
            dataTestId="remove-from-team-modal"
            actions={[
              {
                label: "Cancel",
                variant: "cancel",
                onClick: () => setIsShowingRemoveModal(false),
              },
              {
                label: "Remove",
                variant: "primary",
                onClick: handleConfirmRemoveFromTeam,
                dataTestId: "confirm-button",
              },
            ]}
          >
            <div>
              Are you sure you want to remove{" "}
              <strong>
                {selectedUser?.firstName} {selectedUser?.lastName}
              </strong>{" "}
              from{" "}
              <strong>{team?.name ? team?.name : "the team"}</strong>?
            </div>
            <div className="my-md">
              <DSCardWrapper
                variant="danger"
                padding="p-sm"
                dataTestId="remove-user-danger-alert"
              >
                This means you will no longer be able to view their
                notes, templates, or use the phone numbers they have
                shared with the team. If you are paying for their
                subscription they will be downgraded to a free account
                and your billing will be adjusted.
              </DSCardWrapper>
            </div>
          </DSModal>
        )}
        {isShowingDeactivateModal && (
          <DSModal
            isOpen={isShowingDeactivateModal}
            onClose={() => setIsShowingDeactivateModal(false)}
            title={`Deactivate ${selectedUser?.firstName} ${selectedUser?.lastName}`}
            dataTestId="deactivate-user-modal"
            actions={[
              {
                label: "Cancel",
                variant: "cancel",
                onClick: () => setIsShowingDeactivateModal(false),
              },
              {
                label: "Deactivate",
                variant: "primary",
                onClick: handleConfirmDeactivateUser,
                dataTestId: "confirm-button",
              },
            ]}
          >
            <div>
              Are you sure you want to deactivate the account of{" "}
              <strong>
                {selectedUser?.firstName} {selectedUser?.lastName}
              </strong>
              ? <br />
              <div className="mt-md">
                <DSCardWrapper
                  variant="danger"
                  padding="p-sm"
                  dataTestId="deactivate-user-danger-alert"
                >
                  This will lock them out of their Scribenote account
                  and you will need to contact our support team to
                  reactivate it.
                </DSCardWrapper>
              </div>
            </div>
          </DSModal>
        )}
      </DSCardWrapper>
    </div>
  );
}

function TableHeader({
  sortBy,
  title,
  sortUsers = () => {},
  isCentered = false,
}) {
  return (
    <th
      className={`font-semibold text-sm cursor-pointer ${
        isCentered ? "text-center pr-4" : "text-left"
      }`}
      onClick={sortBy ? () => sortUsers(sortBy) : null}
    >
      {title}
      {sortBy && (
        <FontAwesomeIcon icon={faSort} size="xs" className="ml-1" />
      )}
    </th>
  );
}
